import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'main',
    'mainZoom',
    'mainZoomInner',
    'thumb',
    'counter',
    'lightbox',
    'lightboxImage',
    'lightboxViewport',
    'zoomLevel'
  ]

  static values = {
    index: { type: Number, default: 0 },
    hoverZoomScale: { type: Number, default: 2.25 }
  }

  connect () {
    this.lightboxOpen = false
    this.lightboxEl = null
    this.lightboxImageEl = null
    this.lightboxViewportEl = null
    this.zoomLevelEl = null
    this.lightboxPlaceholder = null
    this.zoom = 1
    this.panX = 0
    this.panY = 0
    this.touchStartX = 0
    this.touchStartY = 0
    this.pinchStartDistance = 0
    this.pinchStartZoom = 1
    this.dragging = false
    this.dragStartX = 0
    this.dragStartY = 0
    this.dragPanX = 0
    this.dragPanY = 0
    this.hoverZoomEnabled = window.matchMedia('(hover: hover)').matches
    this.hoverZoomActive = false

    this.boundKeydown = this.onKeydown.bind(this)
    document.addEventListener('keydown', this.boundKeydown)

    this.cacheLightboxElements()
    this.bindLightboxEvents()

    if (this.hasMainTarget) this.show(this.indexValue)
  }

  disconnect () {
    document.removeEventListener('keydown', this.boundKeydown)
    this.unbindLightboxEvents()
    this.closeLightbox()
    this.restoreLightboxHome()
  }

  cacheLightboxElements () {
    if (!this.hasLightboxTarget) return

    this.lightboxEl = this.lightboxTarget
    this.lightboxImageEl = this.hasLightboxImageTarget ? this.lightboxImageTarget : null
    this.lightboxViewportEl = this.hasLightboxViewportTarget ? this.lightboxViewportTarget : null
    this.zoomLevelEl = this.hasZoomLevelTarget ? this.zoomLevelTarget : null
  }

  bindLightboxEvents () {
    if (!this.lightboxEl || this.lightboxEventsBound) return

    this.onLightboxClick = this.onLightboxClick.bind(this)
    this.onLightboxWheel = this.onWheel.bind(this)
    this.onLightboxTouchStart = this.onTouchStart.bind(this)
    this.onLightboxTouchMove = this.onTouchMove.bind(this)
    this.onLightboxMouseDown = this.onMouseDown.bind(this)
    this.onLightboxMouseMove = this.onMouseMove.bind(this)
    this.onLightboxMouseUp = this.onMouseUp.bind(this)

    this.lightboxEl.addEventListener('click', this.onLightboxClick)
    this.lightboxEl.addEventListener('wheel', this.onLightboxWheel, { passive: false })
    this.lightboxEl.addEventListener('touchstart', this.onLightboxTouchStart, { passive: true })
    this.lightboxEl.addEventListener('touchmove', this.onLightboxTouchMove, { passive: false })
    this.lightboxEl.addEventListener('mousedown', this.onLightboxMouseDown)
    this.lightboxEl.addEventListener('mousemove', this.onLightboxMouseMove)
    this.lightboxEl.addEventListener('mouseup', this.onLightboxMouseUp)
    this.lightboxEl.addEventListener('mouseleave', this.onLightboxMouseUp)

    this.lightboxEventsBound = true
  }

  unbindLightboxEvents () {
    if (!this.lightboxEl || !this.lightboxEventsBound) return

    this.lightboxEl.removeEventListener('click', this.onLightboxClick)
    this.lightboxEl.removeEventListener('wheel', this.onLightboxWheel)
    this.lightboxEl.removeEventListener('touchstart', this.onLightboxTouchStart)
    this.lightboxEl.removeEventListener('touchmove', this.onLightboxTouchMove)
    this.lightboxEl.removeEventListener('mousedown', this.onLightboxMouseDown)
    this.lightboxEl.removeEventListener('mousemove', this.onLightboxMouseMove)
    this.lightboxEl.removeEventListener('mouseup', this.onLightboxMouseUp)
    this.lightboxEl.removeEventListener('mouseleave', this.onLightboxMouseUp)

    this.lightboxEventsBound = false
  }

  onLightboxClick (event) {
    if (!this.lightboxOpen) return

    const actionEl = event.target.closest('[data-lightbox-action]')
    if (actionEl) {
      event.stopPropagation()
      switch (actionEl.dataset.lightboxAction) {
        case 'close':
          this.closeLightbox(event)
          break
        case 'zoom-in':
          this.zoomIn(event)
          break
        case 'zoom-out':
          this.zoomOut(event)
          break
        case 'prev':
          this.prev(event)
          break
        case 'next':
          this.next(event)
          break
      }
      return
    }

    if (event.target === this.lightboxEl) {
      this.closeLightbox(event)
    }
  }

  ensureLightboxInBody () {
    if (!this.lightboxEl || this.lightboxEl.parentNode === document.body) return

    this.lightboxPlaceholder = document.createComment('ad-gallery-lightbox')
    this.lightboxEl.parentNode.insertBefore(this.lightboxPlaceholder, this.lightboxEl)
    document.body.appendChild(this.lightboxEl)
  }

  restoreLightboxHome () {
    if (!this.lightboxEl || !this.lightboxPlaceholder?.parentNode) return

    this.lightboxPlaceholder.parentNode.insertBefore(this.lightboxEl, this.lightboxPlaceholder)
    this.lightboxPlaceholder.remove()
    this.lightboxPlaceholder = null
  }

  thumbUrl (thumb, kind) {
    if (kind === 'full') {
      return thumb.dataset.fullUrl || thumb.getAttribute('data-full-url') || ''
    }

    return thumb.dataset.previewUrl || thumb.getAttribute('data-preview-url') || ''
  }

  get urls () {
    if (!this.hasThumbTarget) return []

    return this.thumbTargets.map((thumb) => ({
      preview: this.thumbUrl(thumb, 'preview'),
      full: this.thumbUrl(thumb, 'full')
    }))
  }

  get count () {
    return this.urls.length
  }

  currentImageUrl (kind = 'full') {
    const item = this.urls[this.indexValue]
    const fromItem = kind === 'full' ? item?.full : item?.preview
    if (fromItem) return fromItem

    if (!this.hasMainTarget) return null

    if (kind === 'full') {
      return this.mainTarget.dataset.fullUrl ||
        this.mainTarget.getAttribute('data-full-url') ||
        this.mainTarget.currentSrc ||
        this.mainTarget.src ||
        null
    }

    return this.mainTarget.currentSrc || this.mainTarget.src || null
  }

  prev (event) {
    if (event) event.stopPropagation()
    if (this.count < 2) return
    this.go(this.indexValue - 1)
  }

  next (event) {
    if (event) event.stopPropagation()
    if (this.count < 2) return
    this.go(this.indexValue + 1)
  }

  goTo (event) {
    const index = Number(event.currentTarget.dataset.index)
    if (Number.isNaN(index)) return
    this.go(index)
  }

  go (index) {
    if (this.count === 0) return
    this.indexValue = ((index % this.count) + this.count) % this.count
    this.show(this.indexValue)
  }

  show (index) {
    if (!this.hasMainTarget) return

    const item = this.urls[index]
    const preview = item?.preview || this.currentImageUrl('preview')
    const full = item?.full || this.currentImageUrl('full')
    if (!preview && !full) return

    if (preview) this.mainTarget.src = preview
    if (full) this.mainTarget.dataset.fullUrl = full
    this.resetMainHoverZoom()

    this.thumbTargets.forEach((thumb, i) => {
      const active = i === index
      thumb.classList.toggle('ring-2', active)
      thumb.classList.toggle('ring-brand-500', active)
      thumb.classList.toggle('ring-offset-2', active)
      thumb.classList.toggle('ring-1', !active)
      thumb.classList.toggle('ring-slate-200', !active)
      thumb.setAttribute('aria-pressed', active ? 'true' : 'false')
    })

    if (this.hasCounterTarget && this.count > 0) {
      this.counterTarget.textContent = `${index + 1} / ${this.count}`
    }

    const activeThumb = this.thumbTargets[index]
    if (activeThumb) {
      activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
    }

    if (this.lightboxOpen && this.lightboxImageEl) {
      const lightboxUrl = full || preview
      if (lightboxUrl) this.lightboxImageEl.src = lightboxUrl
      this.resetLightboxTransform()
    }
  }

  openLightbox (event) {
    if (event) event.stopPropagation()
    if (!this.lightboxEl || !this.lightboxImageEl) return

    const url = this.currentImageUrl('full') || this.currentImageUrl('preview')
    if (!url) return

    this.resetMainHoverZoom()
    this.ensureLightboxInBody()
    this.lightboxOpen = true
    this.zoom = 1
    this.panX = 0
    this.panY = 0
    this.lightboxImageEl.src = url
    this.lightboxEl.classList.remove('hidden')
    this.lightboxEl.setAttribute('aria-hidden', 'false')
    document.body.classList.add('overflow-hidden')
    this.applyLightboxTransform()
    this.updateZoomLabel()
  }

  closeLightbox (event) {
    if (event) event.stopPropagation()
    if (!this.lightboxEl) return

    this.lightboxOpen = false
    this.lightboxEl.classList.add('hidden')
    this.lightboxEl.setAttribute('aria-hidden', 'true')
    document.body.classList.remove('overflow-hidden')
    this.resetLightboxTransform()
    this.restoreLightboxHome()
  }

  zoomIn (event) {
    if (event) event.stopPropagation()
    this.setZoom(this.zoom + 0.5)
  }

  zoomOut (event) {
    if (event) event.stopPropagation()
    this.setZoom(this.zoom - 0.5)
  }

  resetZoom (event) {
    if (event) event.stopPropagation()
    this.setZoom(1)
  }

  onWheel (event) {
    if (!this.lightboxOpen) return
    event.preventDefault()
    const delta = event.deltaY < 0 ? 0.25 : -0.25
    this.setZoom(this.zoom + delta)
  }

  onTouchStart (event) {
    if (!this.lightboxOpen) {
      if (event.touches.length === 1) {
        this.touchStartX = event.touches[0].clientX
        this.touchStartY = event.touches[0].clientY
      }
      return
    }

    if (event.touches.length === 2) {
      this.pinchStartDistance = this.touchDistance(event.touches)
      this.pinchStartZoom = this.zoom
    } else if (event.touches.length === 1 && this.zoom > 1) {
      this.dragging = true
      this.dragStartX = event.touches[0].clientX
      this.dragStartY = event.touches[0].clientY
      this.dragPanX = this.panX
      this.dragPanY = this.panY
    }
  }

  onTouchMove (event) {
    if (!this.lightboxOpen) return

    if (event.touches.length === 2) {
      event.preventDefault()
      const distance = this.touchDistance(event.touches)
      const scale = distance / this.pinchStartDistance
      this.setZoom(this.pinchStartZoom * scale)
    } else if (this.dragging && event.touches.length === 1) {
      event.preventDefault()
      const dx = event.touches[0].clientX - this.dragStartX
      const dy = event.touches[0].clientY - this.dragStartY
      this.panX = this.dragPanX + dx
      this.panY = this.dragPanY + dy
      this.applyLightboxTransform()
    }
  }

  onTouchEnd (event) {
    if (this.lightboxOpen) {
      this.dragging = false
      return
    }

    if (this.count < 2 || event.changedTouches.length !== 1) return

    const dx = event.changedTouches[0].clientX - this.touchStartX
    const dy = event.changedTouches[0].clientY - this.touchStartY
    if (Math.abs(dx) < 48 || Math.abs(dy) > Math.abs(dx)) return

    if (dx > 0) this.prev()
    else this.next()
  }

  onMouseDown (event) {
    if (!this.lightboxOpen || this.zoom <= 1) return
    if (event.target.closest('[data-lightbox-action]')) return
    event.preventDefault()
    this.dragging = true
    this.dragStartX = event.clientX
    this.dragStartY = event.clientY
    this.dragPanX = this.panX
    this.dragPanY = this.panY
  }

  onMouseMove (event) {
    if (!this.dragging) return
    this.panX = this.dragPanX + (event.clientX - this.dragStartX)
    this.panY = this.dragPanY + (event.clientY - this.dragStartY)
    this.applyLightboxTransform()
  }

  onMouseUp () {
    this.dragging = false
  }

  onKeydown (event) {
    if (this.lightboxOpen) {
      if (event.key === 'Escape') {
        event.preventDefault()
        this.closeLightbox()
      } else if (event.key === '+' || event.key === '=') {
        event.preventDefault()
        this.zoomIn()
      } else if (event.key === '-') {
        event.preventDefault()
        this.zoomOut()
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault()
        this.prev()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        this.next()
      }
      return
    }

    if (event.key === 'ArrowLeft') this.prev()
    else if (event.key === 'ArrowRight') this.next()
  }

  setZoom (value) {
    const next = Math.min(4, Math.max(1, value))
    if (next === 1) {
      this.panX = 0
      this.panY = 0
    }
    this.zoom = next
    this.applyLightboxTransform()
    this.updateZoomLabel()
  }

  applyLightboxTransform () {
    if (!this.lightboxImageEl) return
    this.lightboxImageEl.style.transform = `translate(${this.panX}px, ${this.panY}px) scale(${this.zoom})`
  }

  resetLightboxTransform () {
    if (!this.lightboxImageEl) return
    this.zoom = 1
    this.panX = 0
    this.panY = 0
    this.lightboxImageEl.style.transform = ''
    this.updateZoomLabel()
  }

  updateZoomLabel () {
    if (!this.zoomLevelEl) return
    this.zoomLevelEl.textContent = `${Math.round(this.zoom * 100)}%`
  }

  touchDistance (touches) {
    const dx = touches[0].clientX - touches[1].clientX
    const dy = touches[0].clientY - touches[1].clientY
    return Math.hypot(dx, dy)
  }

  onMainHoverEnter (event) {
    if (!this.hoverZoomEnabled || !this.hasMainTarget) return
    this.hoverZoomActive = true
    const fullUrl = this.currentImageUrl('full')
    if (fullUrl && this.mainTarget.src !== fullUrl) {
      this.mainTarget.src = fullUrl
    }
    if (this.hasMainZoomTarget) {
      this.mainZoomTarget.classList.add('product-gallery-viewer--zooming')
    }
    this.applyMainHoverZoom(event)
  }

  onMainHoverMove (event) {
    if (!this.hoverZoomActive) return
    this.applyMainHoverZoom(event)
  }

  applyMainHoverZoom (event) {
    if (!this.hasMainZoomInnerTarget) return

    const container = this.hasMainZoomTarget ? this.mainZoomTarget : this.mainZoomInnerTarget.parentElement
    if (!container) return

    const rect = container.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) return

    const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100))
    const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100))

    this.mainZoomInnerTarget.style.transformOrigin = `${x}% ${y}%`
    this.mainZoomInnerTarget.style.transform = `scale(${this.hoverZoomScaleValue})`
  }

  onMainHoverLeave () {
    this.hoverZoomActive = false
    this.resetMainHoverZoom()
  }

  resetMainHoverZoom () {
    if (this.hasMainZoomInnerTarget) {
      this.mainZoomInnerTarget.style.transform = ''
      this.mainZoomInnerTarget.style.transformOrigin = 'center center'
    }
    if (!this.hasMainTarget) return
    const preview = this.currentImageUrl('preview')
    if (preview && this.mainTarget.src !== preview) {
      this.mainTarget.src = preview
    }
    if (this.hasMainZoomTarget) {
      this.mainZoomTarget.classList.remove('product-gallery-viewer--zooming')
    }
  }
}
